import { useEffect, useState } from "react";
import { Plus, Save, Trash2, X, Eye, EyeOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const EMPTY = {
    title: "",
    slug: "",
    excerpt: "",
    content_md: "",
    cover_url: "",
    author: "",
    tags: [],
    published_at: "",
    visible: true,
};

export default function AdminActualites() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [tagInput, setTagInput] = useState("");

    useEffect(() => {
        document.title = "Admin · L'actualité PA";
        reload();
    }, []);

    const reload = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/actualites");
            setItems(data || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const startNew = () => setEditing({ ...EMPTY, _new: true });
    const startEdit = (a) => setEditing({ ...a });
    const cancelEdit = () => { setEditing(null); setTagInput(""); };

    const addTag = () => {
        const v = tagInput.trim();
        if (!v) return;
        if (editing.tags.includes(v)) return;
        setEditing({ ...editing, tags: [...editing.tags, v] });
        setTagInput("");
    };
    const removeTag = (t) =>
        setEditing({ ...editing, tags: editing.tags.filter((x) => x !== t) });

    const save = async () => {
        if (!editing.title?.trim()) { toast.error("Titre obligatoire."); return; }
        setSaving(true);
        try {
            const payload = {
                title: editing.title,
                slug: editing.slug || null,
                excerpt: editing.excerpt || "",
                content_md: editing.content_md || "",
                cover_url: editing.cover_url || "",
                author: editing.author || "",
                tags: editing.tags || [],
                published_at: editing.published_at || null,
                visible: !!editing.visible,
            };
            if (editing._new) {
                await api.post("/admin/actualites", payload);
                toast.success("Article créé.");
            } else {
                await api.put(`/admin/actualites/${editing.id}`, payload);
                toast.success("Article mis à jour.");
            }
            cancelEdit();
            reload();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer cet article ?")) return;
        try {
            await api.delete(`/admin/actualites/${id}`);
            toast.success("Article supprimé.");
            reload();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const toggleVisible = async (a) => {
        try {
            await api.put(`/admin/actualites/${a.id}`, {
                title: a.title, slug: a.slug, excerpt: a.excerpt, content_md: a.content_md,
                cover_url: a.cover_url, author: a.author, tags: a.tags,
                published_at: a.published_at, visible: !a.visible,
            });
            reload();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="space-y-6" data-testid="admin-actualites-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-display text-3xl text-brand-ink">L'actualité PA</h1>
                    <p className="mt-1 text-sm text-brand-smoke">Articles affichés sur <code>/actualite-pa</code>.</p>
                </div>
                <button onClick={startNew} data-testid="actualites-new-btn"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c]">
                    <Plus className="h-4 w-4" /> Nouvel article
                </button>
            </div>

            {loading ? (
                <div className="py-12 text-center text-sm text-brand-smoke">Chargement…</div>
            ) : items.length === 0 ? (
                <div className="rounded-xl border border-dashed border-brand-line bg-[#FAFAFA] py-12 text-center text-sm text-brand-smoke">
                    Aucun article. Cliquez sur « Nouvel article » pour publier le premier contenu.
                </div>
            ) : (
                <div className="space-y-3" data-testid="actualites-list">
                    {items.map((a, i) => (
                        <div key={a.id} className="flex flex-wrap items-center gap-4 rounded-xl border border-brand-line bg-white p-4" data-testid={`actualite-row-${i}`}>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <strong className="truncate text-brand-ink">{a.title}</strong>
                                    {!a.visible && <span className="rounded-full bg-brand-smoke/20 px-2 py-0.5 text-[10px] uppercase text-brand-smoke">Masqué</span>}
                                </div>
                                <p className="truncate text-xs text-brand-smoke">/{a.slug} · {a.published_at ? new Date(a.published_at).toLocaleDateString("fr-FR") : "non publié"} {a.author && `· ${a.author}`}</p>
                            </div>
                            <div className="flex gap-1.5">
                                <a href={`/actualite-pa/${a.slug}`} target="_blank" rel="noreferrer" className="rounded-full border border-brand-line p-2 hover:border-brand-ink" aria-label="Voir">
                                    <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                                <button onClick={() => toggleVisible(a)} className="rounded-full border border-brand-line p-2 hover:border-brand-ink" aria-label="Visibilité">
                                    {a.visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                                </button>
                                <button onClick={() => startEdit(a)} className="rounded-full bg-brand-ink px-4 py-2 text-xs font-semibold text-white hover:bg-brand-coal">Éditer</button>
                                <button onClick={() => remove(a.id)} className="rounded-full border border-red-200 p-2 text-red-600 hover:bg-red-50" aria-label="Supprimer">
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <div className="fixed inset-0 z-50 flex items-end justify-end bg-black/40 backdrop-blur-sm" onClick={cancelEdit}>
                    <div className="h-full w-full max-w-2xl overflow-y-auto bg-white p-8 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="actualites-edit-drawer">
                        <div className="flex items-center justify-between">
                            <h2 className="font-display text-2xl text-brand-ink">{editing._new ? "Nouvel article" : "Éditer l'article"}</h2>
                            <button onClick={cancelEdit} className="rounded-full p-2 hover:bg-brand-line/40" aria-label="Fermer"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="mt-6 space-y-4">
                            <Field label="Titre *">
                                <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="actualites-input-title" className="input" />
                            </Field>
                            <Field label="Slug (auto si vide)">
                                <input value={editing.slug || ""} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="ex: prevention-active-2026" className="input" />
                            </Field>
                            <Field label="Résumé (excerpt)">
                                <textarea rows={3} value={editing.excerpt || ""} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className="input" />
                            </Field>
                            <Field label="Contenu (Markdown)">
                                <textarea rows={10} value={editing.content_md || ""} onChange={(e) => setEditing({ ...editing, content_md: e.target.value })} placeholder="# Titre&#10;&#10;Paragraphe…&#10;&#10;- Point 1&#10;- Point 2" className="input font-mono text-xs" data-testid="actualites-input-content" />
                            </Field>
                            <Field label="Image de couverture (URL)">
                                <input value={editing.cover_url || ""} onChange={(e) => setEditing({ ...editing, cover_url: e.target.value })} placeholder="https://…" className="input" />
                            </Field>
                            <div className="grid grid-cols-2 gap-4">
                                <Field label="Auteur"><input value={editing.author || ""} onChange={(e) => setEditing({ ...editing, author: e.target.value })} placeholder="Dr. X" className="input" /></Field>
                                <Field label="Date de publication"><input type="datetime-local" value={editing.published_at ? editing.published_at.slice(0, 16) : ""} onChange={(e) => setEditing({ ...editing, published_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="input" /></Field>
                            </div>
                            <Field label="Tags">
                                <div className="flex gap-2">
                                    <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())} placeholder="Ex: étude, événement" className="input flex-1" />
                                    <button type="button" onClick={addTag} className="rounded-xl bg-brand-ink px-4 py-2.5 text-sm font-semibold text-white">+</button>
                                </div>
                                {editing.tags?.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {editing.tags.map((t) => (
                                            <span key={t} className="inline-flex items-center gap-1 rounded-full border border-brand-orange/30 bg-brand-orange/5 px-2.5 py-1 text-xs">
                                                {t}
                                                <button onClick={() => removeTag(t)} className="ml-1 hover:text-red-600">×</button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </Field>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={!!editing.visible} onChange={(e) => setEditing({ ...editing, visible: e.target.checked })} className="h-4 w-4 accent-brand-orange" />
                                Visible publiquement
                            </label>
                            <div className="flex gap-3 pt-4">
                                <button onClick={save} disabled={saving} data-testid="actualites-save-btn" className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-brand-orange px-5 py-3 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-60">
                                    <Save className="h-4 w-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
                                </button>
                                <button onClick={cancelEdit} className="rounded-full border border-brand-line px-5 py-3 text-sm font-semibold">Annuler</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`.input { width:100%; border-radius:0.75rem; border:1px solid #E5E5E5; padding:0.625rem 1rem; font-size:0.875rem; outline:none; } .input:focus { border-color:#E8873C; box-shadow:0 0 0 3px rgba(232,135,60,0.2); }`}</style>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">{label}</label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}
